import { FC, useState } from 'react';
import {
    Box,
    Typography,
    Stack,
    Divider,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Chip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { FormSectionDto, FormQuestionDto } from '../onsite-visit.dto';
import { QuestionRenderer } from './QuestionRenderer';
import { Building } from '../onsite-visit.dto';

interface SectionRendererProps {
    section: FormSectionDto;
    responses: Record<string, string>;
    errors: Record<string, boolean>;
    onResponseChange: (questionId: string, value: string) => void;
    // Building selection props - now per instance
    selectedBuildings?: Building[] | null;
    instanceEntityMap?: Record<string, string[]>;
    onInstanceEntityChange?: (instanceKey: string, entityIds: string[]) => void;
    formatSelectedIds?: (selected: string[], buildings: Building[] | null) => string;
    t?: (key: string) => string;
}

export const SectionRenderer: FC<SectionRendererProps> = ({
    section,
    responses,
    errors,
    onResponseChange,
    selectedBuildings,
    instanceEntityMap,
    onInstanceEntityChange,
    formatSelectedIds,
    t = (key: string) => key,
}) => {
    const sectionKey = `section-${section.sectionIdentifier}`;
    const isEntityTarget = section.targetEntity === 'Building';

    // Track which instances are expanded (first one by default)
    const [expandedInstances, setExpandedInstances] = useState<Set<number>>(new Set([0]));

    const toggleInstance = (index: number) => {
        setExpandedInstances((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const getInstanceCompletionStatus = (instance: FormQuestionDto[], instanceIndex: number) => {
        const instanceKey = `${sectionKey}-instance-${instanceIndex}`;

        // Count only required fields
        const requiredQuestions = instance.filter((q) => q.config?.required);
        const totalFields = requiredQuestions.length + (isEntityTarget ? 1 : 0);
        let completedFields = 0;

        // Check building assignment (always required if entity-targeted)
        const entities = instanceEntityMap?.[instanceKey] || [];
        if (isEntityTarget && entities.length > 0) {
            completedFields++;
        }

        // Check only required question responses
        requiredQuestions.forEach((question) => {
            const value = responses[question.id] || question.response || '';
            if (value.trim()) {
                completedFields++;
            }
        });

        return { completedFields, totalFields };
    };

    const getBuildingName = (instanceKey: string): string | null => {
        const entities = instanceEntityMap?.[instanceKey] || [];
        if (entities.length === 0 || !selectedBuildings) return null;

        // For multiple selections, show count
        if (entities.length > 1) {
            return `${entities.length} Building${entities.length > 1 ? 's' : ''} selected`;
        }

        const entityId = entities[0];
        const building = selectedBuildings.find((b) => b.id === entityId);
        if (building) return building.code;

        return null;
    };

    // Format section title - convert snake_case to Title Case
    const formatSectionTitle = (identifier: string): string => {
        return identifier.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    return (
        <Box sx={{ mb: 3 }}>
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    📋 {formatSectionTitle(section.sectionIdentifier)}
                </Typography>
                <Chip
                    label={`${section.instances.length} instance${section.instances.length > 1 ? 's' : ''}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            </Box>

            <Stack spacing={2}>
                {section.instances.map((instance, instanceIndex) => {
                    const instanceKey = `${sectionKey}-instance-${instanceIndex}`;
                    const buildingName = getBuildingName(instanceKey);
                    const { completedFields, totalFields } = getInstanceCompletionStatus(instance, instanceIndex);
                    const isComplete = completedFields === totalFields;
                    const hasError = errors[instanceKey] || instance.some((q) => errors[q.id]);
                    const isExpanded = expandedInstances.has(instanceIndex);

                    return (
                        <Accordion
                            key={instanceIndex}
                            id={instanceKey}
                            expanded={isExpanded}
                            onChange={() => toggleInstance(instanceIndex)}
                            sx={{
                                border: '1px solid',
                                borderColor: 'divider',
                                borderLeft: '4px solid',
                                borderLeftColor: hasError
                                    ? 'error.main'
                                    : buildingName
                                      ? 'success.main'
                                      : 'warning.main',
                                borderRadius: '12px !important',
                                overflow: 'hidden',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                                mb: 2,
                                '&:before': { display: 'none' },
                                '&:hover': {
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                                },
                                transition: 'all 0.3s ease',
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    '& .MuiAccordionSummary-content': {
                                        alignItems: 'center',
                                        gap: 2,
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', mr: 2 }}>
                                    {isComplete ? (
                                        <CheckCircleIcon color="success" />
                                    ) : hasError ? (
                                        <ErrorIcon color="error" />
                                    ) : (
                                        <LocationOnIcon color="disabled" />
                                    )}

                                    <Box sx={{ flex: 1 }}>
                                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                            {buildingName || `Instance ${instanceIndex + 1}`}
                                        </Typography>
                                        {buildingName && (
                                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                Instance {instanceIndex + 1}
                                            </Typography>
                                        )}
                                    </Box>

                                    <Chip
                                        label={`${completedFields}/${totalFields}`}
                                        size="small"
                                        color={isComplete ? 'success' : hasError ? 'error' : 'default'}
                                        sx={{ minWidth: 60 }}
                                    />
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails>
                                {/* Instance-level Building/Entity Selection */}
                                {isEntityTarget && onInstanceEntityChange && (
                                    <FormControl fullWidth margin="normal" size="small" error={errors[instanceKey]}>
                                        <InputLabel id={`instance-select-label-${instanceKey}`}>
                                            {t(`Choose ${section.targetEntity}(s)`)} *
                                        </InputLabel>
                                        <Select
                                            labelId={`instance-select-label-${instanceKey}`}
                                            multiple
                                            value={instanceEntityMap?.[instanceKey] || []}
                                            label={t(`Choose ${section.targetEntity}(s)`) + ' *'}
                                            size="small"
                                            onChange={(event) => {
                                                const value = event.target.value;
                                                const selectedValues = typeof value === 'string' ? [value] : value;
                                                onInstanceEntityChange(instanceKey, selectedValues);
                                            }}
                                            renderValue={(selected) =>
                                                formatSelectedIds
                                                    ? formatSelectedIds(selected, selectedBuildings ?? null)
                                                    : selected.join(', ')
                                            }
                                        >
                                            {selectedBuildings?.map((building) => (
                                                <MenuItem key={building.id} value={building.id}>
                                                    {building.code}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                        {errors[instanceKey] && (
                                            <Typography color="error" variant="body2" sx={{ mt: 0.5 }}>
                                                {t('This selection is required')}
                                            </Typography>
                                        )}
                                    </FormControl>
                                )}

                                <Divider sx={{ mb: 2 }} />

                                <Stack spacing={2}>
                                    {instance.map((question: FormQuestionDto) => (
                                        <Box key={question.id}>
                                            <QuestionRenderer
                                                question={question}
                                                value={responses[question.id] || question.response || ''}
                                                onChange={(value) => onResponseChange(question.id, value)}
                                                error={errors[question.id]}
                                                helperText={errors[question.id] ? t('This field is required') : ''}
                                                t={t}
                                            />
                                        </Box>
                                    ))}
                                </Stack>
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Stack>
        </Box>
    );
};
